import { memo, useRef, useState } from 'react';
import { ButtonEl } from 'components/site/site_sections';
import styled, { createGlobalStyle } from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import MuxPlayer from 'components/reusable/MuxPlayer/mux-player';
import Button from 'components/library/Button';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
import Icon from 'components/global/Icon/Primary';
import { mqSmallMin } from 'styled/tools/media-queries';

const UsageStyles = createGlobalStyle`
  .pds08-usage {
  max-width: none !important;
  padding: 0 !important;

  > .container {
    max-width: 1440px;
    padding: 30px;
    margin: 0 auto;
    position: relative;

    @media ${mqLargeMin} {
      padding: 110px 50px 0px 50px;
    }
  }

  .top {
    > * {
      color: var(--text-primary);
    }

    margin-bottom: 30px;

    @media ${mqLargeMin} {
      margin-bottom: 50px;
    }
  }

  h2 {
    margin-bottom: 13.5px;

    span,
    i {
      display: block;
      font-size: 26px;
      font-weight: 300;
      color: var(--text-primary);
      line-height: 1.1;
    }

    @media ${mqLargeMin} {
      margin-bottom: 23.5px;
      span,
      i {
        font-size: 50px;
      }
    }
  }

  .bg-video-m {
    @media ${mqSmallMin} {
      display: none;
    }
  }
  .bg-video-d {
    display: none;
    @media ${mqSmallMin} {
      display: block;
    }
  }

  span {
    color: var(--text-primary) !important;
  }

  .pause-play-white {
    path {
      stroke: var(--fill-primary);
      color: var(--text-inverse-primary);
      fill: var(--fill-primary);
    }
    margin-right: 6px;
    margin-left: 6px;
  }
}

`;

const PauseButtonContainer = styled.div`
  position: absolute;
  bottom: var(--spacing-16);
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  max-width: 1440px;
  margin: 0 auto;
  padding-right: var(--spacing-16);
`;

const PDS08Usage = () => {
  const mobileVideoRefs = useRef<HTMLVideoElement[]>([]);
  const [isMobilePlaying, setIsMobilePlaying] = useState(true);
  const [isDesktopPlaying, setIsDesktopPlaying] = useState(true);
  const desktopVideoRefs = useRef<HTMLVideoElement[]>([]);

  const handleMobilePlayPauseClick = () => {
    if (mobileVideoRefs.current) {
      if (isMobilePlaying) {
        mobileVideoRefs.current[0]?.pause();
        setIsMobilePlaying(false);
        buttonEventHandler('Pause video', 'PDS08 Usage');
      } else {
        mobileVideoRefs.current[0]?.play();
        setIsMobilePlaying(true);
        buttonEventHandler('Play video', 'PDS08 Usage');
      }
    }
  };

  const handleDesktopPlayPauseClick = () => {
    if (desktopVideoRefs.current) {
      if (isDesktopPlaying) {
        desktopVideoRefs.current[0]?.pause();
        setIsDesktopPlaying(false);
        buttonEventHandler('Pause video', 'PDS08 Usage');
      } else {
        desktopVideoRefs.current[0]?.play();
        setIsDesktopPlaying(true);
        buttonEventHandler('Play video', 'PDS08 Usage');
      }
    }
  };

  return (
    <section className='pds08-usage'>
      <UsageStyles />
      <div className='container'>
        <div className='top'>
          <h2>
            <span>Designed with</span>
            <i>how-do-I-get-my-kid-to-take-this-daily</i>
            <span>in mind.</span>
          </h2>

          <ButtonEl
            data={{
              fields: {
                icon: 'plus_circle',
                altIcon: 'minus_circle',
                label: 'How to use, daily.',
                modal: 'modal-pds08-usage',
              },
            }}
          />
        </div>
        <div id='video' className='poster'>
          <div className='bg-video-m' style={{ position: 'relative' }}>
            <MuxPlayer
              playbackId={
                process.env.NEXT_PUBLIC_STAGING === 'true'
                  ? 'S6NaZ9G5Hhiq01Vig7tpMSiTluLITr8jvT7f64Ig02BsU'
                  : '6oo7PIhtm017UeUxkpOLcd6VB3wwVVjeQGAYrt3UrWms'
              }
              autoPlay
              muted
              loop
              videoRefs={mobileVideoRefs}
              index={0}
              // @ts-ignore
              style={{ aspectRatio: 1, '--controls': 'none' }}
            />
            <PauseButtonContainer>
              <div>
                <Button
                  color='frostedglass'
                  size='small'
                  onClick={handleMobilePlayPauseClick}
                >
                  {isMobilePlaying ? (
                    <Icon
                      name='pause'
                      size='md'
                      className='pause-play-white'
                      aria-label='Pause'
                    />
                  ) : (
                    <Icon
                      name='play'
                      size='md'
                      className='pause-play-white'
                      aria-label='Play'
                    />
                  )}
                </Button>
              </div>
            </PauseButtonContainer>
          </div>
          <div className='bg-video-d' style={{ position: 'relative' }}>
            <MuxPlayer
              playbackId={
                process.env.NEXT_PUBLIC_STAGING === 'true'
                  ? 'yIxM9Md00YReVHA1To5ZUbHwcd01PLAI1C9J02YfiJTubo'
                  : 'QfwYVlaMkxXx7i8mA4yX1KLDfO1qwwb4ZSNWn4hoyDE'
              }
              autoPlay
              muted
              loop
              videoRefs={desktopVideoRefs}
              index={0}
              // @ts-ignore
              style={{ aspectRatio: 16 / 9, '--controls': 'none' }}
            />
            <PauseButtonContainer>
              <div>
                <Button
                  color='frostedglass'
                  size='small'
                  onClick={handleDesktopPlayPauseClick}
                >
                  {isDesktopPlaying ? (
                    <Icon
                      name='pause'
                      size='md'
                      className='pause-play-white'
                      aria-label='Pause'
                    />
                  ) : (
                    <Icon
                      name='play'
                      size='md'
                      className='pause-play-white'
                      aria-label='Play'
                    />
                  )}
                </Button>
              </div>
            </PauseButtonContainer>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(PDS08Usage);
